export type WalletOperation =
  | 'BUY'
  | 'SELL'
  | 'CONVERT'
  | 'SCHEDULE'
  | 'DEPOSIT'
  | 'WITHDRAWAL'
  | 'TRANSFER';

export type AmountOperation = 'buy' | 'sell' | 'withdrawal' | 'deposit' | 'transfer';

export type CapabilityLimits = {
  minAmountBuy?: number;
  maxAmountBuy?: number;
  limitAmountBuy?: number;
  minAmountSell?: number;
  maxAmountSell?: number;
  limitAmountSell?: number;
  minAmountWithdrawal?: number;
  maxAmountWithdrawal?: number;
  limitAmountWithdrawal?: number;
  minAmountDeposit?: number;
  maxAmountDeposit?: number;
  limitAmountDeposit?: number;
  minAmountTransfer?: number;
  maxAmountTransfer?: number;
  limitAmountTransfer?: number;
};

export type UserCapabilities = {
  limits?: CapabilityLimits;
};

export type AmountLimits = {
  minUsd?: number;
  maxUsd?: number;
};

export type ProfiledWallet = {
  walletNumber: string;
  descriptionCurrency: string;
  codeCurrency: string;
  availableBalance: number;
  accountBalance: number;
  estimateUsd: number;
  exchangeRate: number;
  label?: string;
  glosa?: string | null;
  icon?: string | null;
  decimals?: number;
  pairs?: string[];
  networks?: string[];
  allowedOperations?: string[];
};

const ACTION_ALIASES: Record<string, WalletOperation> = {
  BUY: 'BUY',
  SELL: 'SELL',
  CONVERT: 'CONVERT',
  CONVERSION: 'CONVERT',
  SCHEDULE: 'SCHEDULE',
  SCHEDULED: 'SCHEDULE',
  PROGRAM: 'SCHEDULE',
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  WITHDRAW: 'WITHDRAWAL',
  TRANSFER: 'TRANSFER',
  P2P: 'TRANSFER',
};

const LIMIT_KEYS = {
  'min-amount-buy': 'minAmountBuy',
  'max-amount-buy': 'maxAmountBuy',
  'limit-amount-buy': 'limitAmountBuy',
  'min-amount-sell': 'minAmountSell',
  'max-amount-sell': 'maxAmountSell',
  'limit-amount-sell': 'limitAmountSell',
  'min-amount-withdrawal': 'minAmountWithdrawal',
  'max-amount-withdrawal': 'maxAmountWithdrawal',
  'limit-amount-withdrawal': 'limitAmountWithdrawal',
  'min-amount-deposit': 'minAmountDeposit',
  'max-amount-deposit': 'maxAmountDeposit',
  'limit-amount-deposit': 'limitAmountDeposit',
  'min-amount-transfer': 'minAmountTransfer',
  'max-amount-transfer': 'maxAmountTransfer',
  'limit-amount-transfer': 'limitAmountTransfer',
} as const;

function normalizeAction(value: string): WalletOperation | null {
  return ACTION_ALIASES[value.trim().toUpperCase()] ?? null;
}

function parseNumber(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value !== 'string') {
    return 0;
  }
  const parsed = Number(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseOptionalAmount(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function parseStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
}

/** Sin `allowedOperations`, o con la lista vacía, la wallet no opera. */
export function walletAllowsOperation(
  wallet: { allowedOperations?: string[] | null },
  action: WalletOperation,
): boolean {
  const operations = wallet.allowedOperations;
  if (!operations || operations.length === 0) {
    return false;
  }
  return operations.some((item) => normalizeAction(item) === action);
}

export function walletsForOperation<T extends { allowedOperations?: string[] }>(
  wallets: readonly T[],
  action: WalletOperation,
): T[] {
  return wallets.filter((wallet) => walletAllowsOperation(wallet, action));
}

export function anyWalletAllowsOperation(
  wallets: readonly { allowedOperations?: string[] }[],
  action: WalletOperation,
): boolean {
  return wallets.some((wallet) => walletAllowsOperation(wallet, action));
}

export function canSend(
  wallets: readonly { allowedOperations?: string[] }[],
): boolean {
  return (
    anyWalletAllowsOperation(wallets, 'WITHDRAWAL') ||
    anyWalletAllowsOperation(wallets, 'TRANSFER')
  );
}

export function parseProfiledWallets(payload: unknown): ProfiledWallet[] {
  if (!Array.isArray(payload)) {
    return [];
  }
  return payload.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const record = item as Record<string, unknown>;
    const codeCurrency =
      typeof record['codeCurrency'] === 'string' ? record['codeCurrency'].trim() : '';
    const walletNumber =
      typeof record['walletNumber'] === 'string' ? record['walletNumber'].trim() : '';
    if (!codeCurrency || !walletNumber) {
      return [];
    }
    const allowed = parseStringList(record['allowedOperations']);
    const pairs = parseStringList(record['pairs']);
    const networks = parseStringList(record['networks']);
    const label = typeof record['label'] === 'string' ? record['label'].trim() : '';
    const icon = typeof record['icon'] === 'string' ? record['icon'].trim() : '';
    const glosa = typeof record['glosa'] === 'string' ? record['glosa'] : null;
    const decimals = parseOptionalAmount(record['decimals']);
    return [
      {
        walletNumber,
        descriptionCurrency:
          typeof record['descriptionCurrency'] === 'string' && record['descriptionCurrency'].trim()
            ? record['descriptionCurrency'].trim()
            : codeCurrency,
        codeCurrency,
        availableBalance: parseNumber(record['availableBalance']),
        accountBalance: parseNumber(record['accountBalance']),
        estimateUsd: parseNumber(record['estimateUsd']),
        exchangeRate: parseNumber(record['exchangeRate']),
        ...(label ? { label } : {}),
        ...(icon ? { icon } : {}),
        ...(glosa != null ? { glosa } : {}),
        ...(decimals != null ? { decimals } : {}),
        ...(pairs ? { pairs } : {}),
        ...(networks ? { networks } : {}),
        ...(allowed ? { allowedOperations: allowed } : {}),
      },
    ];
  });
}

export function parseUserCapabilities(value: unknown): UserCapabilities | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const rawLimits = record['limits'];
  if (!rawLimits || typeof rawLimits !== 'object' || Array.isArray(rawLimits)) {
    return null;
  }
  const source = rawLimits as Record<string, unknown>;
  const limits: CapabilityLimits = {};
  (Object.keys(LIMIT_KEYS) as Array<keyof typeof LIMIT_KEYS>).forEach((key) => {
    const amount = parseOptionalAmount(source[key]);
    if (amount != null) {
      limits[LIMIT_KEYS[key]] = amount;
    }
  });
  return Object.keys(limits).length > 0 ? { limits } : null;
}

function restrictiveMax(...values: Array<number | undefined>): number | undefined {
  const defined = values.filter((value): value is number => value != null);
  return defined.length > 0 ? Math.min(...defined) : undefined;
}

export function getAmountLimits(
  capabilities: UserCapabilities | null | undefined,
  operation: AmountOperation,
): AmountLimits {
  const limits = capabilities?.limits;
  if (!limits) {
    return {};
  }
  if (operation === 'buy') {
    return {
      minUsd: limits.minAmountBuy,
      maxUsd: restrictiveMax(limits.maxAmountBuy, limits.limitAmountBuy),
    };
  }
  if (operation === 'sell') {
    return {
      minUsd: limits.minAmountSell,
      maxUsd: restrictiveMax(limits.maxAmountSell, limits.limitAmountSell),
    };
  }
  if (operation === 'withdrawal') {
    return {
      minUsd: limits.minAmountWithdrawal,
      maxUsd: restrictiveMax(limits.maxAmountWithdrawal, limits.limitAmountWithdrawal),
    };
  }
  if (operation === 'deposit') {
    return {
      minUsd: limits.minAmountDeposit,
      maxUsd: restrictiveMax(limits.maxAmountDeposit, limits.limitAmountDeposit),
    };
  }
  return {
    minUsd: limits.minAmountTransfer,
    maxUsd: restrictiveMax(limits.maxAmountTransfer, limits.limitAmountTransfer),
  };
}

export function amountLimitMessage(
  limits: AmountLimits,
  amountUsd: number,
  noun: string,
): string | null {
  if (limits.minUsd != null && amountUsd > 0 && amountUsd < limits.minUsd) {
    return `El monto mínimo de ${noun} es de US$ ${limits.minUsd}.`;
  }
  if (limits.maxUsd != null && amountUsd > limits.maxUsd) {
    return `El monto máximo de ${noun} es de US$ ${limits.maxUsd}.`;
  }
  return null;
}

export function walletDisplayName(wallet: {
  label?: string;
  descriptionCurrency: string;
  codeCurrency: string;
}): string {
  return wallet.label?.trim() || wallet.descriptionCurrency || wallet.codeCurrency;
}

export function isQuoteCurrency(code: string): boolean {
  return /^(USDC|USDT|USD|PEN|TUSDC|TUSDT)$/i.test(code.trim());
}
